import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bot, Database, Globe, LifeBuoy, Mic, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useGetAddOnPricesQuery,
  useGetAddOnsQuery,
  useGetAiListingChargesQuery,
  useGetHomeQuery,
  useGetMembersQuery,
  useGetSubscriptionQuery,
  useSetAddOnMutation,
} from "@/store/rest_api";
import { AddOnType, DialerSeatLine } from "@/model/interface/AddOn";
import { DialerSeat } from "@/enum/DialerSeat";
import { MembershipRole } from "@/enum/MembershipRole";
import { MembershipStatus } from "@/enum/MembershipStatus";
import { PowerDialerDialog } from "@/components/pages/billing/addons/PowerDialerDialog";
import { PropertyDataDialog } from "@/components/pages/billing/addons/PropertyDataDialog";
import { SimpleAddOnDialog } from "@/components/pages/billing/addons/SimpleAddOnDialog";
import { formatAddOnPrice, formatPeriodEnd, renewingSeats, seatCount } from "@/util/addOns";
import { apiErrorMessage } from "@/util/apiError";
import { useFeatures } from "@/hooks/useFeature";
import { usePropertyDataAtCost } from "@/hooks/useAddOn";

type AddOnKind = "power-dialer" | "ai-agents" | "property-data" | "call-recording" | "pro-support" | "website-builder";

type SimpleAddOnKind = "call-recording" | "pro-support" | "website-builder";

interface AddOnEntry {
  kind: AddOnKind;
  icon: React.ReactNode;
  name: string;
  description: string;
  detail: string;
  offer: string;
  badge?: string;
  active: boolean;
  canceling: boolean;
  currentCents: number;
  renewingCents: number;
}

const SIMPLE_ADD_ONS: Record<
  SimpleAddOnKind,
  { addOnType: AddOnType; title: string; inlineName: string; description: string; turnOffNote: (endsOn: string) => string }
> = {
  "call-recording": {
    addOnType: AddOnType.CALL_RECORDING,
    title: "Call recording",
    inlineName: "call recording",
    description: "Record, keep and replay calls from the dialer and power dialer.",
    turnOffNote: () =>
      "New calls stop recording right away. Recordings you already have stay playable and still count toward storage until you delete them.",
  },
  "pro-support": {
    addOnType: AddOnType.PRO_SUPPORT,
    title: "Pro support",
    inlineName: "Pro support",
    description: "Priority support with faster replies.",
    turnOffNote: (endsOn) => `You keep priority support until ${endsOn}, then it won't renew.`,
  },
  "website-builder": {
    addOnType: AddOnType.WEBSITE_BUILDER,
    title: "Website builder",
    inlineName: "website builder",
    description: "SEO lead-capture sites on your own subdomain.",
    turnOffNote: (endsOn) => `Your sites stay live until ${endsOn}, then they unpublish and it won't renew.`,
  },
};

const AI_TIERS = [
  { addOnType: AddOnType.AI_AGENT_INBOUND_BASIC, label: "inbound Basic" },
  { addOnType: AddOnType.AI_AGENT_INBOUND_MEDIUM, label: "inbound Medium" },
  { addOnType: AddOnType.AI_AGENT_INBOUND_PREMIUM, label: "inbound Premium" },
  { addOnType: AddOnType.AI_AGENT_OUTBOUND_BASIC, label: "outbound Basic" },
  { addOnType: AddOnType.AI_AGENT_OUTBOUND_MEDIUM, label: "outbound Medium" },
  { addOnType: AddOnType.AI_AGENT_OUTBOUND_PREMIUM, label: "outbound Premium" },
];

const PROPERTY_TIERS = [AddOnType.PROPERTY_DATA_5K, AddOnType.PROPERTY_DATA_10K];

function monthlyLabel(cents: number): string {
  return cents === 0 ? "Free" : `${formatAddOnPrice(cents)}/mo`;
}

function AddOnIcon({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
      {children}
    </span>
  );
}

interface AddOnRowProps {
  entry: AddOnEntry;
  endLabel: string | null;
  action: React.ReactNode;
}

function AddOnRow({ entry, endLabel, action }: AddOnRowProps) {
  return (
    <div className="grid grid-cols-[2.25rem_minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 px-4 py-3 sm:grid-cols-[2.25rem_minmax(0,1fr)_7rem_12rem]">
      <AddOnIcon>{entry.icon}</AddOnIcon>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{entry.name}</span>
          {entry.canceling && <span className="status-pill-warning">{endLabel ? `Ends ${endLabel}` : "Ending"}</span>}
        </div>
        <p className="text-sm text-muted-foreground">{entry.detail}</p>
      </div>
      <p className="text-right text-sm font-semibold tabular-nums">{monthlyLabel(entry.currentCents)}</p>
      <div className="col-span-3 flex justify-end sm:col-span-1">{action}</div>
    </div>
  );
}

interface AddOnCardProps {
  entry: AddOnEntry;
  action?: React.ReactNode;
}

function AddOnCard({ entry, action }: AddOnCardProps) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <AddOnIcon>{entry.icon}</AddOnIcon>
        {entry.badge && <span className="status-pill-neutral">{entry.badge}</span>}
      </div>
      <div className="space-y-0.5">
        <p className="font-semibold">{entry.name}</p>
        <p className="text-sm text-muted-foreground">{entry.description}</p>
      </div>
      <div className="mt-auto flex items-center justify-between gap-3">
        <p className="text-sm tabular-nums">{entry.offer}</p>
        {action}
      </div>
    </div>
  );
}

export function AddOns() {
  const navigate = useNavigate();
  const { data: home } = useGetHomeQuery();
  const { data: addOns, isLoading: isLoadingAddOns } = useGetAddOnsQuery();
  const { data: prices, isLoading: isLoadingPrices } = useGetAddOnPricesQuery();
  const { data: listingCharges } = useGetAiListingChargesQuery();
  const { data: members = [] } = useGetMembersQuery({});
  const organisation = home?.activeOrganisation;
  const onTrial = organisation?.subscriptionStatus === "TRIAL" || organisation?.subscriptionStatus === "TRIAL_LAPSED";
  const { data: subscription } = useGetSubscriptionQuery(undefined, {
    skip: onTrial || !organisation?.stripeSubscriptionId,
  });
  const [setAddOn, { isLoading: isKeeping }] = useSetAddOnMutation();
  const [openDialog, setOpenDialog] = useState<AddOnKind | null>(null);

  const membership = home?.activeMembership;
  const canAssignSeats =
    membership?.status === MembershipStatus.ACTIVE &&
    (membership.role === MembershipRole.OWNER || membership.role === MembershipRole.ADMIN);
  const endDate = formatPeriodEnd(subscription?.currentPeriodEnd);
  const endLabel = formatPeriodEnd(subscription?.currentPeriodEnd, "MMM d");
  const aiFeatures = useFeatures(["AI_VOICE_AGENTS_INBOUND", "AI_VOICE_AGENTS_OUTBOUND"]);
  const aiIncluded = aiFeatures.AI_VOICE_AGENTS_INBOUND || aiFeatures.AI_VOICE_AGENTS_OUTBOUND;
  const isPropertyDataAtCost = usePropertyDataAtCost();

  const quantityOf = (addOnType: AddOnType) => addOns?.find((addOn) => addOn.addOnType === addOnType)?.quantity ?? 0;
  const cancelingOf = (addOnType: AddOnType) =>
    addOns?.find((addOn) => addOn.addOnType === addOnType)?.cancelAtPeriodEnd ?? false;
  const priceCentsOf = (addOnType: AddOnType) =>
    prices?.find((entry) => entry.addOnType === addOnType)?.effectiveMonthlyCostCents ?? 0;
  const seatLineOf = (addOnType: AddOnType, seat: DialerSeat): DialerSeatLine => ({
    quantity: quantityOf(addOnType),
    canceling: cancelingOf(addOnType),
    assigned: members.filter((member) => member.dialerSeat === seat).length,
    priceCents: priceCentsOf(addOnType),
  });

  const single = seatLineOf(AddOnType.POWER_DIALER_SINGLE, DialerSeat.SINGLE_LINE);
  const multi = seatLineOf(AddOnType.POWER_DIALER_MULTI, DialerSeat.MULTI_LINE);
  const propertyTiers = PROPERTY_TIERS.filter((tier) => quantityOf(tier) > 0);
  const propertyTier = propertyTiers.find((tier) => !cancelingOf(tier)) ?? propertyTiers[0] ?? null;
  const propertyCanceling = !!propertyTier && cancelingOf(propertyTier);
  const aiParts = AI_TIERS.map((tier) => ({ ...tier, quantity: quantityOf(tier.addOnType) })).filter(
    (tier) => tier.quantity > 0,
  );
  const privateAgents = (listingCharges ?? []).reduce((sum, charge) => sum + charge.quantity, 0);

  const powerDialerEntry = (): AddOnEntry => {
    const seats = single.quantity + multi.quantity;
    const renewing = renewingSeats(single) + renewingSeats(multi);
    const seatParts = [
      single.quantity > 0 ? seatCount(single.quantity, "single-line") : null,
      multi.quantity > 0 ? seatCount(multi.quantity, "multi-line") : null,
    ].filter(Boolean);
    return {
      kind: "power-dialer",
      icon: <PhoneCall className="h-4 w-4" />,
      name: "Power dialer",
      description: "Work through your lists hands-free, one line or up to three at once.",
      detail: `${seatParts.join(" and ")}, ${single.assigned + multi.assigned} of ${seats} assigned`,
      offer: `From ${formatAddOnPrice(Math.min(single.priceCents, multi.priceCents))} a seat/mo`,
      active: seats > 0,
      canceling: seats > 0 && renewing === 0,
      currentCents: single.quantity * single.priceCents + multi.quantity * multi.priceCents,
      renewingCents: renewingSeats(single) * single.priceCents + renewingSeats(multi) * multi.priceCents,
    };
  };

  const aiAgentsEntry = (): AddOnEntry => {
    const tierCents = aiParts.reduce((sum, tier) => sum + tier.quantity * priceCentsOf(tier.addOnType), 0);
    const listingCents = (listingCharges ?? []).reduce((sum, charge) => sum + charge.monthlyCostCents * charge.quantity, 0);
    const detailParts = [
      ...aiParts.map((tier) => `${tier.quantity} ${tier.label}`),
      privateAgents > 0 ? `${privateAgents} with ${privateAgents === 1 ? "its" : "their"} own price` : null,
    ].filter(Boolean);
    return {
      kind: "ai-agents",
      icon: <Bot className="h-4 w-4" />,
      name: "AI voice agents",
      description: "Agents that answer your line and call your leads for you.",
      detail: `${detailParts.join(", ")}, plus talk time`,
      offer: `From ${formatAddOnPrice(priceCentsOf(AddOnType.AI_AGENT_INBOUND_BASIC))} an agent/mo, plus talk time`,
      badge: aiIncluded ? undefined : "Pro plan",
      active: detailParts.length > 0,
      canceling: false,
      currentCents: tierCents + listingCents,
      renewingCents: tierCents + listingCents,
    };
  };

  const propertyDataEntry = (): AddOnEntry => {
    if (isPropertyDataAtCost) {
      return {
        kind: "property-data",
        icon: <Database className="h-4 w-4" />,
        name: "Property data",
        description: "Included in your plan. Every lead you import is billed at our provider cost, no quota.",
        detail: "Billed per lead",
        offer: "Pay per lead at cost",
        badge: "Included",
        active: false,
        canceling: false,
        currentCents: 0,
        renewingCents: 0,
      };
    }
    const cents = propertyTier ? priceCentsOf(propertyTier) : 0;
    return {
      kind: "property-data",
      icon: <Database className="h-4 w-4" />,
      name: "Property data",
      description: "Skip-traced leads every month. $0.13 a lead beyond your quota.",
      detail: propertyTier === AddOnType.PROPERTY_DATA_10K ? "1,350 leads a month" : "400 leads a month",
      offer: `From ${formatAddOnPrice(priceCentsOf(AddOnType.PROPERTY_DATA_5K))}/mo`,
      active: !!propertyTier,
      canceling: propertyCanceling,
      currentCents: cents,
      renewingCents: propertyCanceling ? 0 : cents,
    };
  };

  const simpleEntry = (kind: SimpleAddOnKind, icon: React.ReactNode, detail: string, offer: string): AddOnEntry => {
    const addOnType = SIMPLE_ADD_ONS[kind].addOnType;
    const quantity = quantityOf(addOnType);
    const canceling = quantity > 0 && cancelingOf(addOnType);
    const cents = quantity * priceCentsOf(addOnType);
    return {
      kind,
      icon,
      name: SIMPLE_ADD_ONS[kind].title,
      description: SIMPLE_ADD_ONS[kind].description,
      detail,
      offer,
      active: quantity > 0,
      canceling,
      currentCents: cents,
      renewingCents: canceling ? 0 : cents,
    };
  };

  const websiteSites = quantityOf(AddOnType.WEBSITE_BUILDER);
  const entries: AddOnEntry[] = [
    powerDialerEntry(),
    aiAgentsEntry(),
    propertyDataEntry(),
    simpleEntry(
      "call-recording",
      <Mic className="h-4 w-4" />,
      "Plus storage for every recorded minute you keep",
      `${monthlyLabel(priceCentsOf(AddOnType.CALL_RECORDING))}, plus storage`,
    ),
    simpleEntry(
      "pro-support",
      <LifeBuoy className="h-4 w-4" />,
      "Priority support with faster replies",
      monthlyLabel(priceCentsOf(AddOnType.PRO_SUPPORT)),
    ),
    simpleEntry(
      "website-builder",
      <Globe className="h-4 w-4" />,
      `${websiteSites} published ${websiteSites === 1 ? "site" : "sites"}`,
      `${monthlyLabel(priceCentsOf(AddOnType.WEBSITE_BUILDER))} per site`,
    ),
  ];
  const activeEntries = entries.filter((entry) => entry.active);
  const availableEntries = entries.filter((entry) => !entry.active);
  const renewingTotal = activeEntries.reduce((sum, entry) => sum + entry.renewingCents, 0);

  const keepUpdates = (kind: AddOnKind): { addOnType: AddOnType; quantity: number }[] => {
    if (kind === "power-dialer") {
      return [
        { addOnType: AddOnType.POWER_DIALER_SINGLE, quantity: single.quantity },
        { addOnType: AddOnType.POWER_DIALER_MULTI, quantity: multi.quantity },
      ].filter((update) => update.quantity > 0);
    }
    if (kind === "property-data") {
      return PROPERTY_TIERS.map((tier) => ({ addOnType: tier, quantity: tier === propertyTier ? 1 : 0 }));
    }
    if (kind === "ai-agents") {
      return [];
    }
    return [{ addOnType: SIMPLE_ADD_ONS[kind].addOnType, quantity: 1 }];
  };

  const keepAddOn = async (entry: AddOnEntry) => {
    try {
      for (const update of keepUpdates(entry.kind)) {
        await setAddOn(update).unwrap();
      }
      toast.success(`${entry.name} will keep renewing`);
    } catch (error) {
      toast.error(apiErrorMessage(error, `Failed to keep ${entry.name.toLowerCase()}`));
    }
  };

  const rowAction = (entry: AddOnEntry) => {
    if (entry.kind === "ai-agents") {
      return (
        <Link to="/ai-agents" className="text-sm font-medium text-primary hover:underline">
          Manage in AI agents
        </Link>
      );
    }
    if (entry.canceling) {
      return (
        <Button variant="outline" size="sm" disabled={isKeeping} onClick={() => keepAddOn(entry)}>
          Keep
        </Button>
      );
    }
    return (
      <Button variant="outline" size="sm" onClick={() => setOpenDialog(entry.kind)}>
        Manage
      </Button>
    );
  };

  const cardAction = (entry: AddOnEntry) => {
    if (entry.kind === "ai-agents") {
      return (
        <Button asChild variant="outline" size="sm">
          <Link to="/ai-marketplace">Browse agents</Link>
        </Button>
      );
    }
    if (entry.kind === "property-data" && isPropertyDataAtCost) {
      return (
        <Button asChild variant="outline" size="sm">
          <Link to="/properties/listings">Open</Link>
        </Button>
      );
    }
    return (
      <Button variant="outline" size="sm" onClick={() => setOpenDialog(entry.kind)}>
        Add
      </Button>
    );
  };

  if (isLoadingAddOns || isLoadingPrices || !organisation) {
    return (
      <div className="container space-y-4 p-4">
        <Skeleton className="h-12 w-72" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (onTrial) {
    return (
      <div className="container space-y-6 p-4 text-foreground">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight">Add-ons</h1>
            <p className="text-muted-foreground">Add-ons start once you're on a paid plan. Here is everything you can add.</p>
          </div>
          <Button onClick={() => navigate("/manage-organisation/billing/plan")}>Choose a plan</Button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {entries.map((entry) => (
            <AddOnCard key={entry.kind} entry={{ ...entry, badge: entry.kind === "ai-agents" ? "Pro plan" : undefined }} />
          ))}
        </div>
        <p className="text-sm text-muted-foreground">Prices exclude applicable sales tax.</p>
      </div>
    );
  }

  const simpleKind = openDialog && openDialog in SIMPLE_ADD_ONS ? (openDialog as SimpleAddOnKind) : null;
  const simpleOpenEntry = simpleKind ? entries.find((entry) => entry.kind === simpleKind) : undefined;

  return (
    <div className="container space-y-6 p-4 text-foreground">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Add-ons</h1>
        <p className="text-muted-foreground tabular-nums">
          {activeEntries.length === 0
            ? "Nothing added yet. Anything you add is billed with your plan."
            : `${renewingTotal === 0 ? "$0" : formatAddOnPrice(renewingTotal)} a month, billed with your plan${endDate ? ` on ${endDate}` : ""}.`}
        </p>
      </div>

      {activeEntries.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-base font-semibold">Your add-ons</h2>
          <div className="divide-y rounded-lg border bg-card">
            {activeEntries.map((entry) => (
              <AddOnRow key={entry.kind} entry={entry} endLabel={endLabel} action={rowAction(entry)} />
            ))}
          </div>
        </section>
      )}

      {availableEntries.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-base font-semibold">{activeEntries.length > 0 ? "Add more" : "Available add-ons"}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {availableEntries.map((entry) => (
              <AddOnCard key={entry.kind} entry={entry} action={cardAction(entry)} />
            ))}
          </div>
        </section>
      )}

      <p className="text-sm text-muted-foreground">
        Prices exclude applicable sales tax. Turning an add-on off gives no credit: it keeps working until{" "}
        {endDate ?? "your billing date"}, and you can keep it until then.
      </p>

      {openDialog === "power-dialer" && (
        <PowerDialerDialog
          single={single}
          multi={multi}
          members={members}
          canAssign={canAssignSeats}
          endDate={endDate}
          onClose={() => setOpenDialog(null)}
        />
      )}

      {openDialog === "property-data" && (
        <PropertyDataDialog
          activeTier={propertyTier}
          canceling={propertyCanceling}
          smallPriceCents={priceCentsOf(AddOnType.PROPERTY_DATA_5K)}
          largePriceCents={priceCentsOf(AddOnType.PROPERTY_DATA_10K)}
          endDate={endDate}
          onClose={() => setOpenDialog(null)}
        />
      )}

      {simpleKind && simpleOpenEntry && (
        <SimpleAddOnDialog
          addOnType={SIMPLE_ADD_ONS[simpleKind].addOnType}
          title={SIMPLE_ADD_ONS[simpleKind].title}
          inlineName={SIMPLE_ADD_ONS[simpleKind].inlineName}
          description={SIMPLE_ADD_ONS[simpleKind].description}
          priceLabel={simpleOpenEntry.offer}
          isFree={priceCentsOf(SIMPLE_ADD_ONS[simpleKind].addOnType) === 0}
          active={simpleOpenEntry.active}
          canceling={simpleOpenEntry.canceling}
          turnOffNote={SIMPLE_ADD_ONS[simpleKind].turnOffNote(endDate ?? "your billing date")}
          endDate={endDate}
          onClose={() => setOpenDialog(null)}
        />
      )}
    </div>
  );
}
